import React from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Briefcase,
  Inbox,
  LogOut,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';
import { logout as adminLogout, getUsername } from '../../lib/admin-api';
import OMCMarkSquare from '../branding/OMCMarkSquare';

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/applications', label: 'Applications', icon: Briefcase },
  { to: '/admin/contacts', label: 'Contacts', icon: Inbox },
];

export default function AdminLayout({ title, subtitle, children }) {
  const navigate = useNavigate();
  const username = getUsername() || 'Admin';

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-bone-2 bg-card">
        <div className="px-5 py-5 border-b border-bone-2">
          <div className="flex items-center gap-3">
            <OMCMarkSquare size={38} />
            <div>
              <p className="font-industrial font-bold text-sm leading-tight tracking-[0.04em] uppercase">Ozark Mtn. Construction</p>
              <p className="font-mono text-[9px] tracking-[0.22em] text-muted-foreground uppercase mt-0.5">
                Admin Console
              </p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground/75 hover:text-foreground hover:bg-[hsl(var(--bone-2))]'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-bone-2 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--bone-2))] transition-colors"
            data-testid="admin-view-public-site"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View public site
          </a>
          <div className="px-3 py-2 rounded-md bg-[hsl(var(--bone-2))] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-mono tracking-wider uppercase text-muted-foreground">Signed in</p>
              <p className="text-sm font-semibold truncate" data-testid="admin-current-user">{username}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 justify-start"
            onClick={handleLogout}
            data-testid="admin-logout-button"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden border-b border-bone-2 bg-card px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <OMCMarkSquare size={32} />
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
              OMC Admin
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-1.5" data-testid="admin-mobile-logout">
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </Button>
        </div>
        {/* Mobile tabs */}
        <div className="lg:hidden border-b border-bone-2 bg-card px-2 py-2 flex gap-1 overflow-x-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </NavLink>
            );
          })}
        </div>

        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl">
          {title ? (
            <div className="mb-7">
              <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground" data-testid="admin-page-title">
                {title}
              </h1>
              {subtitle ? (
                <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
              ) : null}
            </div>
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
}
